import { useCallback, useEffect, useState } from "react";
import { Logo } from "@/components/Logo";
import { ErrorState, MenuUnavailableState, OfflineState } from "@/components/EmptyStates";
import { useLang } from "@/context/LanguageContext";
import { guestMenuApiBase, isApiMenuSource } from "@/config/menuSource";
import { GuestMenuError, loadGuestMenu, normalizeCardCode } from "@/lib/guestMenu";
import { applyGuestTenant } from "@/lib/guestTenant";
import { GUEST_CARD_KEY, entryScope, resolveGuestEntry, storeGuestEntry, storedGuestEntry } from "@/lib/guestEntry";
import { createEventTracker, setActiveEventTracker } from "@/lib/guestEvents";

export { GUEST_CARD_KEY };

// The card code for this page load. A /c/<code> path always wins and never
// falls back (an invalid or foreign code shows an error, not the previous
// restaurant). Without a /c/ path, the code this tab last loaded is reused so
// a reload of /home or /menu keeps the same restaurant.
export function resolveGuestCardCode(pathname, storage) {
  if (pathname.startsWith("/c/")) {
    try {
      return normalizeCardCode(decodeURIComponent(pathname.slice(3).replace(/\/+$/, "")));
    } catch {
      return null;
    }
  }
  try {
    return normalizeCardCode(storage?.getItem(GUEST_CARD_KEY) ?? null);
  } catch {
    return null;
  }
}

export function safeSessionStorage() {
  try { return window.sessionStorage; } catch { return null; }
}

/** Card code of the restaurant this tab loaded (set only after a successful menu load). */
export function activeGuestCardCode() {
  try { return normalizeCardCode(safeSessionStorage()?.getItem(GUEST_CARD_KEY) ?? null); } catch { return null; }
}

/** Entry ({ cardCode } or { tableToken }) this tab loaded, or null. */
export function activeGuestEntry() {
  return storedGuestEntry(safeSessionStorage());
}

// Real-menu bootstrap. In "mock" mode (development/test) it renders children
// unchanged. In "api" mode nothing below it (session, cart, orders, menu UI)
// mounts until this card's menu has loaded and been applied as the tenant.
export function GuestMenuGate({ children, fetchImpl, pathname, search }) {
  const api = isApiMenuSource();
  const { t } = useLang();
  const [state, setState] = useState(api ? "loading" : "ready");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!api) return undefined;
    let cancelled = false;
    const storage = safeSessionStorage();
    const entry = resolveGuestEntry(pathname ?? window.location.pathname, search ?? window.location.search, storage);
    const doFetch = fetchImpl ?? window.fetch?.bind(window);
    setState("loading");
    loadGuestMenu(entry, { baseUrl: guestMenuApiBase(), fetchImpl: doFetch })
      .then((menu) => {
        if (cancelled) return;
        applyGuestTenant(menu, entry.cardCode ?? null, entryScope(entry));
        storeGuestEntry(storage, entry);
        try {
          // Analytics never blocks or breaks the menu.
          const tracker = createEventTracker({ entry, baseUrl: guestMenuApiBase(), fetchImpl: doFetch });
          setActiveEventTracker(tracker);
          tracker.track("menu.view");
        } catch { setActiveEventTracker(null); }
        setState("ready");
      })
      .catch((error) => {
        if (cancelled) return;
        setState(error instanceof GuestMenuError ? error.reason : "unavailable");
      });
    return () => { cancelled = true; };
  }, [api, attempt, fetchImpl, pathname, search]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  if (state === "ready") return children;
  return (
    <div className="mx-auto flex min-h-screen w-full max-w-md flex-col items-center justify-center bg-background" data-testid="guest-menu-gate">
      <Logo size="lg" />
      {state === "loading" && (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="guest-menu-loading">{t("menuLoading")}</p>
      )}
      {state === "not_found" && <MenuUnavailableState />}
      {state === "network" && <OfflineState onRetry={retry} />}
      {(state === "unavailable" || state === "malformed") && <ErrorState onRetry={retry} />}
    </div>
  );
}
